import React, { useContext, useState, useEffect } from 'react';

import { Badge, DropdownButton, SwitchGroup } from '@ergeon/core-components';
import {
  SignalIcon,
  SignalSlashIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  MoonIcon,
  SunIcon,
  PowerIcon,
  ComputerDesktopIcon,
} from '@ergeon/icons/hero/micro';

import { AgentContext } from '../../../context-providers/AgentContext';
import { AuthContext } from '../../../context-providers/AuthContext';
import { useHeaderStatus } from '../hooks/useHeaderStatus';
import { useHeaderActions } from '../hooks/useHeaderActions';
import { ThemeMode, getSavedTheme, saveTheme, applyTheme, listenToSystemTheme } from '../../../utils/darkMode';
import { formatPhoneNumber } from '../../Customers/utils/formatUtils';

const UserMenu: React.FC = () => {
  const { fullName, phoneNumber, identity } = useContext(AgentContext);
  const { logout } = useContext(AuthContext);
  const { status, agentStatus, statusColor, statusText } = useHeaderStatus();
  const { errorCount, showLogs, showSettings, onConnect, disconnect, hasConnectionInfo, isStatusLocked } = useHeaderActions();
  const [themeMode, setThemeMode] = useState<ThemeMode>('system');

  useEffect(() => {
    const savedTheme = getSavedTheme();
    setThemeMode(savedTheme);
    applyTheme(savedTheme);

    // Listen to system theme changes when in 'system' mode
    const cleanup = listenToSystemTheme(() => {
      if (getSavedTheme() === 'system') {
        applyTheme('system');
      }
    });

    return cleanup;
  }, []);

  const handleThemeChange = (newTheme: ThemeMode) => {
    setThemeMode(newTheme);
    saveTheme(newTheme);
    applyTheme(newTheme);
  };

  const handleLogout = async() => {
    if (agentStatus === 'online') {
      await disconnect();
    }
    logout();
  };

  const getConnectionMenuItem = () => {
    if (status === 'connecting') {
      return {
        label: 'Connecting...',
        icon: <SignalIcon className="size-4" />,
        onClick: disconnect,
        disabled: isStatusLocked,
      };
    }
    if (agentStatus === 'online' || agentStatus === 'on-a-call') {
      return {
        label: 'Go Offline',
        icon: <SignalSlashIcon className="size-4" />,
        onClick: disconnect,
        disabled: isStatusLocked,
      };
    }
    return {
      label: 'Go Online',
      icon: <SignalIcon className="size-4" />,
      onClick: onConnect,
      disabled: !hasConnectionInfo || isStatusLocked,
    };
  };

  const connectionMenuItem = getConnectionMenuItem();

  const themeItems = [
    {
      id: 'light',
      content: <SunIcon className="size-4" title="Light Mode" />,
      onClick: () => handleThemeChange('light'),
    },
    {
      id: 'dark',
      content: <MoonIcon className="size-4" title="Dark Mode" />,
      onClick: () => handleThemeChange('dark'),
    },
    {
      id: 'system',
      content: <ComputerDesktopIcon className="size-4" title="System Theme" />,
      onClick: () => handleThemeChange('system'),
    },
  ];

  const dropdownItems = [
    connectionMenuItem,
    { type: 'Divider' },
    {
      label: 'Call Settings',
      icon: <Cog6ToothIcon className="size-4" />,
      onClick: showSettings,
    },
    {
      label: (
        <span className="flex justify-between items-center gap-2 w-full">
          System Logs
          {errorCount > 0 && <Badge value={errorCount} />}
        </span>
      ),
      icon: <DocumentTextIcon className="size-4" />,
      onClick: showLogs,
    },
    {
      label: <SwitchGroup activeId={themeMode} items={themeItems} size="medium" />,
      onClick: () => {
        // No-op: theme changes are handled by SwitchGroup item clicks
      },
    },
    {
      type: 'GroupTitle',
      label: (
        <span className="flex flex-col gap-1 lowercase">
          {phoneNumber && <p>{formatPhoneNumber(phoneNumber)}</p>}
          <p>{identity}</p>
        </span>
      ),
    },
    {
      label: 'Log out',
      icon: <PowerIcon className="size-4" />,
      onClick: handleLogout,
    },
  ].filter(Boolean);

  const getTwilioStatusColor = () => {
    switch (status) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-orange-500';
      default:
        return 'bg-red-500';
    }
  };

  return (
    <DropdownButton
      alignment="right"
      className="shrink-0"
      flavor="regular"
      hideChevron={false}
      items={dropdownItems}
      label={<span className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${getTwilioStatusColor()}`} />
        {fullName}
      </span>}
      taste="boundless">
      <Badge flavor={statusColor} value={statusText} />
    </DropdownButton>
  );
};

export default UserMenu;
